import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { matchPath } from 'react-router';
import { Link } from 'react-router-dom';
import { List } from 'immutable';
import {
  selectServicesKapp,
  selectSurveyKapp,
  Utils,
  C,
} from '@kineticdata/bundle-common';
import { I18n, slugify } from '@kineticdata/react';
import classNames from 'classnames';

import logo from '../../assets/images/USDA_logo_xs.png';

const CollapsibleSidebarGroup = ({
  isOpen,
  setOpen,
  groupId,
  label,
  icon,
  children,
}) => (
  <>
    <div className="app-sidebar__group app-sidebar__group--static">
      <button onClick={() => setOpen(og => (og === groupId ? null : groupId))}>
        <span className={icon} />
        <span className="title">
          <I18n>{label}</I18n>
        </span>
        <span
          className={classNames('toggle fa fa-angle-up', {
            'fa-rotate-180': isOpen !== groupId,
          })}
        />
      </button>
    </div>
    <div
      className={classNames(
        'app-sidebar__group app-sidebar__group--collapsible',
        { 'd-none': isOpen !== groupId },
      )}
    >
      {children}
    </div>
  </>
);

const SidebarComponent = props => {
  const { pathname, onSidebarAction } = props;
  const [openGroup, setOpenGroup] = useState(null);

  const SidebarLink = useCallback(
    ({
      href,
      to,
      matchFn,
      matchParams = {},
      matchExclude,
      icon,
      badge,
      children,
    }) =>
      !!href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => {
            onSidebarAction(e);
            setOpenGroup(null);
          }}
        >
          <span className={icon} />
          <span className="title">{children}</span>
          {!!badge && (
            <span className="badge badge-pill badge-subtle">{badge}</span>
          )}
        </a>
      ) : (
        <Link
          to={to}
          replace={false}
          className={classNames({
            active:
              (typeof matchFn === 'function'
                ? matchFn(pathname)
                : matchPath(pathname, { path: to, ...matchParams })) &&
              (!matchExclude ||
                !matchPath(pathname, {
                  path: matchExclude,
                  ...matchParams,
                })),
          })}
          onClick={e => {
            onSidebarAction(e);
            setOpenGroup(null);
          }}
        >
          <span className={icon} />
          <span className="title">{children}</span>
          {!!badge && (
            <span className="badge badge-pill badge-subtle">{badge}</span>
          )}
        </Link>
      ),
    [pathname, onSidebarAction],
  );

  const portalPagesRootLinks = List(
    Object.values(
      // Map each group to a group entry and each ungrouped item to itself
      props.portalPages.reduce(
        (links, page) => {
          if (page.meta.groupName) {
            const { [page.meta.groupName]: existingGroup, ...rest } = links;
            return {
              ...rest,
              [page.meta.groupName]: {
                groupName: page.meta.groupName,
                // Use smallest sort order of group
                sortOrder: existingGroup
                  ? Math.min(existingGroup.sortOrder, page.meta.sortOrder)
                  : page.meta.sortOrder,
                // Carry over the icon - these are only defined for predefined groups in the code below
                icon: existingGroup ? existingGroup.icon : undefined,
              },
            };
          } else {
            return {
              ...links,
              [page.slug]: { page, sortOrder: page.meta.sortOrder },
            };
          }
        },
        {
          Monitoring: {
            groupName: 'Monitoring',
            sortOrder: 0,
            icon: 'fa fa-chart-bar',
          },
          Help: {
            groupName: 'Help',
            sortOrder: 10001,
            icon: 'fa fa-info-circle',
          },
        },
      ),
    ),
  ).sortBy(l => l.sortOrder);

  const externalPages = List(
    [
      {
        groupName: 'Monitoring',
        icon: 'chart-line',
        name: 'Customer Dashboards',
        href: 'https://cxodashboard.dl.usda.gov/#/projects/1184',
      },
      {
        groupName: 'Monitoring',
        icon: 'tachometer-alt',
        name: 'Systems Monitoring Dashboard',
        href: props.monitoringUrl,
      },
      {
        groupName: 'Help',
        icon: 'book-open',
        name: 'Knowledge Base',
        href:
          'https://usdagcc.sharepoint.com/sites/DISC-NITC/NITC Infrastructure Operations Division/ems-team.usda.gov/DISCKB/SitePages/Home.aspx',
      },
    ].filter(p => !!p.href),
  );

  return (
    <>
      {!props.mobile &&
        !props.tablet &&
        props.logo &&
        props.logo !== 'Disabled' && (
          <div className="app-sidebar__group app-sidebar__group--static">
            <Link className="logo" to="/">
              <img src={props.logo} alt="Logo" />
            </Link>
          </div>
        )}
      {(props.mobile || props.tablet) && (
        <div className="app-sidebar__group app-sidebar__group--static">
          <button className="toggle" onClick={props.toggleSidebar}>
            <span className={classNames('fa fa-times')} />
          </button>
        </div>
      )}
      <div className="scroll-group">
        <div className="app-sidebar__group">
          <SidebarLink to="/" icon="fa fa-home" matchParams={{ exact: true }}>
            <I18n>Home</I18n>
          </SidebarLink>
          {Utils.isKappVisible(props.servicesKapp) && (
            <>
              <SidebarLink
                to={`/kapps/${props.servicesKapp.slug}`}
                icon="fa fa-folder-open"
                matchFn={pathname =>
                  matchPath(pathname, {
                    path: `/kapps/${props.servicesKapp.slug}`,
                    exact: true,
                  }) ||
                  matchPath(pathname, {
                    path: `/kapps/${props.servicesKapp.slug}/categories`,
                  }) ||
                  (matchPath(pathname, {
                    path: `/kapps/${props.servicesKapp.slug}/forms`,
                  }) &&
                    !matchPath(pathname, {
                      path: `/kapps/${props.servicesKapp.slug}/forms/${
                        C.FORM_SLUG_ISSUE
                      }`,
                    }) &&
                    !matchPath(pathname, {
                      path: `/kapps/${props.servicesKapp.slug}/forms/${
                        C.FORM_SLUG_AGENCY_ISSUE
                      }`,
                    }))
                }
              >
                <I18n>Catalog</I18n>
              </SidebarLink>
              <SidebarLink
                to={`/kapps/${props.servicesKapp.slug}/forms/${
                  props.profile.hasAgreements
                    ? C.FORM_SLUG_ISSUE
                    : C.FORM_SLUG_AGENCY_ISSUE
                }?values[Issue]=Yes`}
                icon="fa fa-exclamation-circle"
                matchFn={pathname =>
                  matchPath(pathname, {
                    path: `/kapps/${props.servicesKapp.slug}/forms/${
                      props.profile.hasAgreements
                        ? C.FORM_SLUG_ISSUE
                        : C.FORM_SLUG_AGENCY_ISSUE
                    }`,
                  })
                }
              >
                <I18n>Report an Issue</I18n>
              </SidebarLink>
              <SidebarLink
                to={`/kapps/${props.servicesKapp.slug}/requests`}
                icon="fa fa-ticket-alt"
                badge={props.requestsCount}
              >
                <I18n>My Requests</I18n>
              </SidebarLink>
              <SidebarLink
                to={`/kapps/${props.servicesKapp.slug}/approvals`}
                icon="fa fa-clipboard-check"
                badge={props.approvalsCount}
              >
                <I18n>My Approvals</I18n>
              </SidebarLink>
              <SidebarLink
                to={`/kapps/${props.servicesKapp.slug}/actions`}
                icon="fa fa-tasks"
                badge={props.actionsCount}
              >
                <I18n>My Actions</I18n>
              </SidebarLink>
            </>
          )}
          {Utils.isKappVisible(props.surveyKapp) && (
            <SidebarLink
              to={`/kapps/${props.surveyKapp.slug}`}
              icon="fa fa-clipboard-list"
              badge={props.surveysCount}
              matchFn={pathname =>
                matchPath(pathname, {
                  path: `/kapps/${props.surveyKapp.slug}`,
                }) &&
                !matchPath(pathname, {
                  path: `/kapps/${props.surveyKapp.slug}/admin`,
                })
              }
            >
              <I18n>My Surveys</I18n>
            </SidebarLink>
          )}
        </div>

        <div className="app-sidebar__group--divider" />

        {portalPagesRootLinks.map(
          ({ groupName, page, icon = 'fa fa-plus-square' }) =>
            page ? (
              <div
                className="app-sidebar__group app-sidebar__group--static"
                key={page.slug}
              >
                <SidebarLink
                  to={`/kapps/${page.kapp.slug}/page/${page.slug}`}
                  icon={`fa fa-fw fa-${page.meta.icon ||
                    'fa-arrow-circle-right'}`}
                >
                  <I18n>{page.name}</I18n>
                </SidebarLink>
              </div>
            ) : groupName ? (
              <CollapsibleSidebarGroup
                key={slugify(groupName)}
                groupId={`${slugify(groupName)}-group`}
                label={groupName}
                icon={icon}
                isOpen={openGroup}
                setOpen={setOpenGroup}
              >
                {externalPages.filter(p => p.groupName === groupName).map(p => (
                  <SidebarLink
                    key={p.name}
                    href={p.href}
                    icon={`fa fa-fw fa-${p.icon || 'fa-arrow-circle-right'}`}
                  >
                    <I18n>{p.name}</I18n>
                  </SidebarLink>
                ))}
                {props.portalPages
                  .filter(p => p.meta.groupName === groupName)
                  .sortBy(p => p.meta.sortOrder)
                  .map(p => (
                    <SidebarLink
                      key={p.slug}
                      to={`/kapps/${p.kapp.slug}/page/${p.slug}`}
                      icon={`fa fa-fw fa-${p.meta.icon ||
                        'fa-arrow-circle-right'}`}
                    >
                      <I18n>{p.name}</I18n>
                    </SidebarLink>
                  ))}
              </CollapsibleSidebarGroup>
            ) : null,
        )}

        <div className="app-sidebar__group app-sidebar__group--static">
          <SidebarLink
            to={`/settings`}
            icon="fa fa-tools"
            matchFn={pathname =>
              matchPath(pathname, {
                path: `/kapps/:slug/(settings|admin)`,
              }) ||
              matchPath(pathname, {
                path: `/(settings|profile)`,
              })
            }
          >
            <I18n>{C.SETTINGS_LABEL}</I18n>
          </SidebarLink>
        </div>
      </div>
    </>
  );
};

export const Sidebar = connect(state => ({
  space: state.app.space,
  profile: state.app.profile,
  kapps: state.app.kapps,
  servicesKapp: selectServicesKapp(state),
  surveyKapp: selectSurveyKapp(state),
  portalPages: state.app.portalPages.filter(
    p => p.meta.isMenuLink && C.PORTAL_PAGE_STATUSES.includes(p.status),
  ),
  pathname: state.router.location.pathname,
  logo: Utils.getAttributeValue(state.app.space, 'Logo', logo),
  monitoringUrl: Utils.getAttributeValue(
    state.app.space,
    C.SPACE_ATTR_MONITORING_URL,
  ),
  requestsCount: state.home.requests.count,
  approvalsCount: state.home.approvals.count,
  actionsCount: state.home.actions.count,
  surveysCount: state.home.surveys.count,
  isSmallLayout: state.layout.size === 'small',
}))(SidebarComponent);
